import { useContext } from 'react'
import { PassContext } from "../../pass-context"
import dataSelector from '../data-selector'
import fieldFormatter from '../fronts/components/field-formatter.js'
import parseHtml from 'html-react-parser'
import sanitize from 'sanitize-html'

const styles = {
  child: {
    padding: 15,
    fontSize: 17
  },
  lastChild: {
    borderBottom: "1px solid #ddd",
    padding: 15,
    fontSize: 17
  }
}

// If content and label can fit on one line then do so.
// If not, then label on one line, content follows...
const Field = ({ field, isLastField }) => {

  try {
    const { instanceData, scannerInstanceData } = useContext(PassContext)

    const resolvedField = dataSelector({ template: { field }, instanceData, scannerInstanceData, path: "field" })

    const label = resolvedField.label ? cleanHtml(resolvedField.label, []) : ""
    const resolvedValue = fieldFormatter({ field, value:resolvedField.value })

    const text = addDataFormatting(resolvedField.type, resolvedValue)
    const key = field.key

    // Different formatting if we have a label to when we do not
    if (label.length) return fieldWithLabel({ key, label, text, isLastField })
    if (!label.length) return fieldWithoutLabel({ key, text, isLastField })
  }
  catch (err) {
    throw err
  }
}

export default Field

const fieldWithLabel = ({ key, label, text, isLastField}) => {
  let length = (label + text).length

  // 30 seems to be about right for when Apple pass will wrap the value onto the line after the label
  if (length > 30) return verticalFieldWithlabel({ key, label, text, isLastField })
  else return horizontalFieldWithlabel({ key, label, text, isLastField })
}

const verticalFieldWithlabel = ({ key, label, text, isLastField}) => (
  <div key={key} style={isLastField ? styles.lastChild : styles.child} className="max-w-full">
      <div className="label">
        {label}
      </div>
      <div className="text">
        {text}
      </div>
  </div>
)

const horizontalFieldWithlabel = ({ key, label, text, isLastField}) => (
  <div key={key} style={isLastField ? styles.lastChild : styles.child} className="flex flex-wrap justify-between max-w-full">
      <div className="label">
        {label}
      </div>
      <div className="text">
      {text}
      </div>
  </div>
)

const fieldWithoutLabel = ({ key, text, isLastField }) => (
  <div key={key} style={isLastField ? styles.lastChild : styles.child} className="flex flex-wrap justify-between max-w-full">
    <div className="text">
      {text}
    </div>
  </div>
)


function addDataFormatting(type, value) {
  switch (type ? type : "text") { // default to text if not specified
    case "text": return formatText(value)
    case "phone": return formatPhone(value)
    case "email": return formatEmail(value)
    case "www": return formatWWW(value)
    case "location": return formatLocation(value)
    case "calendar": return formatCalendar(value)

    case "date" : return value
    case "time" : return value
    case "datetime" : return value
    case "number" : return value
    case "money" : return value
  }
}

const formatPhone = value => (<a href={`tel:${value}`}>{value}</a>)
const formatEmail = value => (<a href={`mailto:${value}`}>{value}</a>)
const formatWWW = value => (<a href={value} target="_blank">{value.replace(/https?:\/\//, '')}</a>)
const formatLocation = value => (<a href={`http://maps.google.com?near=${value}&ll=${value}`} target="_blank">Open Map</a>)
const formatCalendar = value => (<a href={value} target="_blank">Add to Calendar</a>)

const formatText = text => {

  let html1 = cleanHtml(text, ['a', 'br'])  // Strip tags we do not permit
  let html2 = newLinesToBrTags(html1) // Convert /n to <br/>
  let html3 = tabsToNbsp(html2) // Convert /n to <br/>

  // Decided to disable Apple data detections.
  // User has to enter HTML tags for Google, so no benefit to using data detection in Apple
  // Also ensures more consistent passes across the two platforms.

  //  Detect and link phone numbers
  //  let html3 = tagTelephoneNumbers(html2) // Add a tags to telephone numbers

  // Detect and link website links. Supposed to do emails as well, buit does not work...
  //  let html4 = anchorme(html3.replace(/https?:\/\//,'')) // Add a tags to mail, web - remove http/https part

  // Add date detection
  // Add address detection

  return parseHtml(html3 ? html3.toString() : "") // parseHtml allows us to output HMTL tags in React
}


// Removes unwanted HTML tags / attributes
const cleanHtml = (text, allowedTags) =>
  sanitize(text?.toString() ?? "", {
    allowedTags: allowedTags,
    allowedAttributes: {
      'a': ['href', 'target'],
    },
    selfClosing: ['br'],
    disallowedTagsMode: 'discard',
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
  })

const newLinesToBrTags = text => text?.replace(/\n/g, "<br/>")
const tabsToNbsp = text => text?.replace(/\t/g, "&nbsp;&nbsp;&nbsp;")

//const tagTelephoneNumbers = text => text.replace(/(\+?[0-9][0-9()\-\s+]{4,20}[0-9])/g, "<a href='tel:$1'>$1</a>")